/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { isFeatureOn } from "../portalSettings";

/**
 * מילוי אוטומטי של קוד שהגיע בסמס (WebOTP).
 *
 * ## מה קורה כאן
 *
 * `navigator.credentials.get({ otp: ... })` מבקש מהדפדפן להקשיב למסרון הבא. כשמגיע
 * מסרון שהשורה האחרונה בו היא `@<כתובת> #<קוד>`, והכתובת זהה לזו שהעמוד פתוח בה,
 * הדפדפן מציע למשתמש למלא - ובאישורו מחזיר את הקוד. השורה עצמה נכתבת בשרת
 * (`sso-backend: models/WebOtp`), והכתובת שבה נפתרת מה-`Host` מול תמונת הפורטלים
 * ולעולם לא מפרמטר בבקשה.
 *
 * **ההרשאה אינה שלנו.** אין כאן קריאת מסרונים ואין גישה להודעות: הדפדפן קורא, הדפדפן
 * משווה, והמשתמש מאשר. מה שמגיע לכאן הוא מחרוזת שכבר עברה את שלושתם.
 *
 * ## למה זה מקום אחד ולא קריאה בכל מסך
 *
 * שבעה מסכים כאן מקבלים קוד, ולכל אחד מהם אותם ארבעה דברים לזכור: לסגור את ההאזנה
 * ביציאה, לא לפתוח שתיים במקביל, לבלוע `AbortError` שאינו שגיאה, ולא לנסות כלל
 * בדפדפן שאינו תומך. ארבעה דברים כפול שבעה מסכים הם שבע הזדמנויות לשכוח אחד מהם -
 * וכל שכחה כזו היא האזנה שנשארת פתוחה על מסך שהמשתמש כבר עזב.
 */

/**
 * פותח האזנה למסרון הבא, ומחזיר את הפונקציה שסוגרת אותה.
 *
 * **פונקציה רגילה ולא גוף של `useEffect`**, וזה מה שמאפשר לבדוק אותה: כל ההכרעות
 * שיש כאן - מי מותר לו, מה הדפדפן חייב לספק, ומה קורה כשהוא מסרב - הן פה, ואין
 * בהן דבר שתלוי ב-React.
 *
 * מחזיר `undefined` כשלא נפתחה האזנה, כדי שיתאים למה ש-`useEffect` מצפה לו.
 */
export function listenForSmsCode(resellerData, onCode) {
    /**
     * אותה הגדרה שמדליקה את השורה במסרון, ובאותה ברירת מחדל - פעילה.
     *
     * זו אינה אכיפה - אין כאן מה לאכוף, הדפדפן לא היה מוצא התאמה ממילא - אלא הסכמה
     * בין המסך למערכת: ריסיילר שכיבה את השורה אינו מקבל האזנה שאין לה מה לתפוס.
     */
    if (!isFeatureOn(resellerData, 'webOtpAutofill')) return undefined;

    /*
     * שלושה תנאים של הדפדפן, וכולם צריכים להתקיים לפני הקריאה: ה-API קיים, יש
     * `credentials`, וההקשר מאובטח. בלי הבדיקה הקריאה זורקת - בדפדפן שאינו תומך,
     * וגם בהרצה מקומית על http.
     */
    if (typeof window === 'undefined' || !('OTPCredential' in window)) return undefined;
    if (!navigator.credentials || !window.isSecureContext) return undefined;

    const abort = new AbortController();

    navigator.credentials
        .get({ otp: { transport: ['sms'] }, signal: abort.signal })
        .then((credential) => {
            if (abort.signal.aborted) return;
            const code = credential?.code;
            if (code) onCode(String(code));
        })
        .catch(() => {
            /*
             * **נבלע, וכל השגיאות יחד.**
             *
             * הביטול ביציאה מהמסך מגיע לכאן כ-`AbortError`, והוא ההתנהגות התקינה ולא
             * כשל. ומה שאינו ביטול - משתמש שסירב, מסרון שלא הגיע, דפדפן שהחליט אחרת -
             * הוא בדיוק המצב שהמסך כבר יודע לטפל בו: השדה פתוח והקוד מוקלד ביד. הודעת
             * שגיאה כאן הייתה מדווחת על תקלה למי שממילא ממשיך כרגיל.
             */
        });

    return () => abort.abort();
}

/**
 * ההאזנה, קשורה למחזור החיים של המסך.
 *
 * ## שני שלבים, ולא אחד - וזו כל הסיבה שההוק הזה מחזיק state
 *
 * הקוד ממלא את השדה **ומגיש את הטופס**: הדפדפן כבר שאל את הלקוח לפני שמסר אותו,
 * ושדה שהתמלא לבד וממתין ללחיצה נוספת הוא בדיוק הצעד שהמנגנון בא לחסוך.
 *
 * אלא שאי אפשר להגיש באותה נשימה: `setState` אינו מיידי, והגשה שרצה מיד אחריו קוראת
 * את השדה **לפני** שהקוד נכנס אליו - כלומר שולחת את הקוד הקודם, או כלום. לכן המילוי
 * מסמן מונה, וההגשה רצה ב-effect שלו: הוא רץ אחרי שהרינדור נסגר, ולכן `onFilled`
 * שנקרא ממנו הוא הסגירה שכבר רואה את הקוד החדש.
 *
 * **ההגשה עוברת במסלול הרגיל של המסך**, ולא עוקפת אותו: אותם שומרים של `busy`, אותם
 * מונים ואותה קאפצ'ה. מה שנחסך הוא לחיצה, ולא בדיקה.
 *
 * @param active    האם עכשיו ממתינים לקוד. המסך עובר שלבים, וההאזנה נפתחת ונסגרת איתם.
 * @param onCode    מכניס את הקוד שהתקבל לשדה. נקרא לכל היותר פעם אחת לכל האזנה.
 * @param onFilled  מגיש. נקרא אחרי שהמילוי כבר על המסך.
 */
export function useWebOtp(active, onCode, onFilled) {
    const resellerData = useSelector((state) => state.reseller.value);

    /**
     * שתי הקריאות החוזרות דרך refs, ולא כתלויות של ה-effect.
     *
     * מסכים מוסרים כאן פונקציות שנוצרות מחדש בכל רינדור - הן נסגרות על ה-state -
     * ותלות בהן הייתה סוגרת ופותחת את ההאזנה בכל הקשה בשדה. מה שנקרא הוא תמיד
     * האחרונה, וב-`onFilled` זו גם הדרישה: רק הסגירה האחרונה רואה את הקוד שמולא.
     */
    const handler = useRef(onCode);
    handler.current = onCode;
    const complete = useRef(onFilled);
    complete.current = onFilled;

    /** מונה מילויים. משתנה בכל קוד שהתקבל, וזה מה שמריץ את ההגשה. */
    const [filled, setFilled] = useState(0);

    useEffect(() => {
        if (!active) return undefined;
        return listenForSmsCode(resellerData, (code) => {
            handler.current?.(code);
            setFilled((count) => count + 1);
        });
    }, [active, resellerData]);

    useEffect(() => {
        // אפס = עוד לא התקבל קוד. בלי התנאי הזה כל מסך היה מגיש את עצמו בטעינה.
        if (!filled) return;
        complete.current?.();
    }, [filled]);
}
